import { Box, CssBaseline, useMediaQuery } from "@mui/material";
import {
  StyledEngineProvider,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";
import { getTheme } from "Components/Layout/Themes/Themes";
import { OfflineOverlay } from "Components/Layout/OfflineOverlay/OfflineOverlay";
import { getCodeListCollectionAsync } from "State/CodeLists/CodeListReducer";
import { getAppUrl } from "Utils/UrlUtils";
import { type ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Toaster } from "sonner";
import {
  type DefaultTheme,
  ThemeProvider as StyledThemeProvider,
} from "styled-components";
import { SupportButton } from "Components/Support/SupportButton";
import { useSetStatusBarColor } from "Hooks/useSetStatusBarColor";

const onRefresh = () =>
  new Promise((resolve) => {
    window.location.reload();
    setTimeout(resolve, 200);
  });

const isPwa = () =>
  ["fullscreen", "standalone", "minimal-ui"].some(
    (displayMode) =>
      window.matchMedia(`(display-mode: ${displayMode})`).matches,
  );

const ThemeAwareWrapper = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  useSetStatusBarColor();

  return (
    <Box
      display="grid"
      minHeight="100dvh"
      gridTemplateRows="1fr auto"
      sx={{ backgroundColor: theme.colors.gray }}
      justifyContent={isMdUp ? "center" : undefined}
    >
      {children}
    </Box>
  );
};

export const Layout = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<DefaultTheme | null>(null);
  const location = useLocation();
  const appUrl = getAppUrl();
  const dispatch = useDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: When the location changes, we want to reset scroll to top of the page
  useLayoutEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: TS definition of behavior is wrong (https://github.com/Microsoft/TypeScript/issues/28755)
    window.scrollTo({ top: 0, left: 0, behavior: "instant" } as any);
  }, [location.pathname]);

  useEffect(() => {
    dispatch(getCodeListCollectionAsync.request());
  }, [dispatch]);

  useEffect(() => {
    setTheme(getTheme());
  }, []);

  const isOnWhitelistedPage = (path: string) => {
    return path === appUrl("dashboard") || path === appUrl("transactions");
  };

  const isPullable = isPwa() && isOnWhitelistedPage(location.pathname);

  return (
    <>
      {theme && (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <StyledThemeProvider theme={theme}>
              <Toaster richColors position="top-right" />
              <SupportButton />
              <OfflineOverlay />
              <PullToRefresh
                isPullable={isPullable}
                onRefresh={onRefresh}
                resistance={4}
                pullingContent=""
              >
                <ThemeAwareWrapper>{children}</ThemeAwareWrapper>
              </PullToRefresh>
            </StyledThemeProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      )}
    </>
  );
};
